// src/services/financialService.js
// *******************************************************************************
// FINANCIAL SERVICE
// *******************************************************************************
// Centralized service for financial calculations, number formatting, rounding,
// percentages, and currency operations. Designed for invoice and fiscal operations.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { strToNumber } from 'utils/helpers';
import translateX from 'utils/translateX';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

/**
 * Rounding modes for financial calculations
 * @type {Object}
 */
const ROUNDING_MODES = {
  ROUND: 'round', // Standard rounding (banker's rounding at .5)
  CEIL: 'ceil', // Always round up
  FLOOR: 'floor', // Always round down
  TRUNCATE: 'truncate', // Remove decimal places without rounding
};

/**
 * Default decimal places for different value types
 * @type {Object}
 */
const DEFAULT_DECIMALS = {
  CURRENCY: 2,
  PERCENTAGE: 2,
  QUANTITY: 3,
  TAX_RATE: 4,
  UNIT_PRICE: 2,
};

// *******************************************************************************
// ROUNDING FUNCTIONS
// *******************************************************************************

/**
 * Round a number with specified mode and decimal places
 * @param {number} value - Value to round
 * @param {number} [decimals=2] - Number of decimal places
 * @param {string} [mode='round'] - Rounding mode (round, ceil, floor, truncate)
 * @returns {number} Rounded value
 */
function round(value, decimals = 2, mode = ROUNDING_MODES.ROUND) {
  if (value === null || value === undefined || isNaN(value)) return 0;

  const num = Number(value);
  const multiplier = Math.pow(10, decimals);

  switch (mode) {
    case ROUNDING_MODES.CEIL:
      return Math.ceil(num * multiplier) / multiplier;
    case ROUNDING_MODES.FLOOR:
      return Math.floor(num * multiplier) / multiplier;
    case ROUNDING_MODES.TRUNCATE:
      return Math.trunc(num * multiplier) / multiplier;
    case ROUNDING_MODES.ROUND:
    default:
      return Math.round(num * multiplier) / multiplier;
  }
}

/**
 * Round up (ceil) with decimal places
 * @param {number} value - Value to round
 * @param {number} [decimals=2] - Number of decimal places
 * @returns {number} Rounded up value
 */
function roundUp(value, decimals = 2) {
  return round(value, decimals, ROUNDING_MODES.CEIL);
}

/**
 * Round down (floor) with decimal places
 * @param {number} value - Value to round
 * @param {number} [decimals=2] - Number of decimal places
 * @returns {number} Rounded down value
 */
function roundDown(value, decimals = 2) {
  return round(value, decimals, ROUNDING_MODES.FLOOR);
}

/**
 * Truncate decimals without rounding
 * @param {number} value - Value to truncate
 * @param {number} [decimals=2] - Number of decimal places
 * @returns {number} Truncated value
 */
function truncate(value, decimals = 2) {
  return round(value, decimals, ROUNDING_MODES.TRUNCATE);
}

/**
 * Round for currency (always 2 decimal places)
 * @param {number} value - Value to round
 * @param {string} [mode='round'] - Rounding mode
 * @returns {number} Rounded currency value
 */
function roundCurrency(value, mode = ROUNDING_MODES.ROUND) {
  return round(value, DEFAULT_DECIMALS.CURRENCY, mode);
}

/**
 * Fix decimal places without rounding (just ensures precision)
 * @param {number} value - Value to fix
 * @param {number} [decimals=2] - Number of decimal places
 * @returns {number} Fixed value
 */
function fix(value, decimals = 2) {
  if (value === null || value === undefined || isNaN(value)) return 0;
  return Number(Number(value).toFixed(decimals));
}

// *******************************************************************************
// PERCENTAGE FUNCTIONS
// *******************************************************************************

/**
 * Calculate percentage of a value
 * @param {number} value - Base value
 * @param {number} percentage - Percentage to calculate
 * @param {number} [decimals=2] - Decimal places for result
 * @returns {number} Calculated percentage value
 * @example percentageOf(100, 15) => 15
 */
function percentageOf(value, percentage, decimals = 2) {
  if (!value || !percentage || isNaN(value) || isNaN(percentage)) return 0;
  return round((Number(value) * Number(percentage)) / 100, decimals);
}

/**
 * Calculate what percentage one value is of another
 * @param {number} part - The part value
 * @param {number} total - The total value
 * @param {number} [decimals=2] - Decimal places for result
 * @returns {number} Percentage
 * @example valueToPercentage(15, 100) => 15
 */
function valueToPercentage(part, total, decimals = 2) {
  const partNum = strToNumber(part) || 0;
  const totalNum = strToNumber(total) || 0;

  if (!totalNum) return 0;
  return round((partNum / totalNum) * 100, decimals);
}

/**
 * Add percentage to a value (markup)
 * @param {number} value - Base value
 * @param {number} percentage - Percentage to add
 * @param {number} [decimals=2] - Decimal places for result
 * @returns {number} Value with percentage added
 * @example addPercentage(100, 10) => 110
 */
function addPercentage(value, percentage, decimals = 2) {
  if (!value || isNaN(value)) return 0;
  if (!percentage || isNaN(percentage)) return round(Number(value), decimals);
  return round(Number(value) * (1 + Number(percentage) / 100), decimals);
}

/**
 * Subtract percentage from a value (discount)
 * @param {number} value - Base value
 * @param {number} percentage - Percentage to subtract
 * @param {number} [decimals=2] - Decimal places for result
 * @returns {number} Value with percentage subtracted
 * @example subtractPercentage(100, 10) => 90
 */
function subtractPercentage(value, percentage, decimals = 2) {
  if (!value || isNaN(value)) return 0;
  if (!percentage || isNaN(percentage)) return round(Number(value), decimals);
  return round(Number(value) * (1 - Number(percentage) / 100), decimals);
}

/**
 * Calculate markup percentage between cost and sale price
 * @param {number} cost - Cost value
 * @param {number} salePrice - Sale price
 * @param {number} [decimals=2] - Decimal places for result
 * @returns {number} Markup percentage
 * @example calculateMarkup(80, 100) => 25
 */
function calculateMarkup(cost, salePrice, decimals = 2) {
  const costNum = strToNumber(cost) || 0;
  const salePriceNum = strToNumber(salePrice) || 0;

  if (!costNum) return 0;
  return round(((salePriceNum - costNum) / costNum) * 100, decimals);
}

/**
 * Calculate margin percentage (profit margin)
 * @param {number} cost - Cost value
 * @param {number} salePrice - Sale price
 * @param {number} [decimals=2] - Decimal places for result
 * @returns {number} Margin percentage
 * @example calculateMargin(80, 100) => 20
 */
function calculateMargin(cost, salePrice, decimals = 2) {
  const costNum = strToNumber(cost) || 0;
  const salePriceNum = strToNumber(salePrice) || 0;

  if (!salePriceNum) return 0;
  return round(((salePriceNum - costNum) / salePriceNum) * 100, decimals);
}

/**
 * Calculate sale price from cost and markup
 * @param {number} cost - Cost value
 * @param {number} markup - Markup percentage
 * @param {number} [decimals=2] - Decimal places for result
 * @returns {number} Sale price
 * @example salePriceFromMarkup(80, 25) => 100
 */
function salePriceFromMarkup(cost, markup, decimals = 2) {
  return addPercentage(cost, markup, decimals);
}

/**
 * Calculate sale price from cost and desired margin
 * @param {number} cost - Cost value
 * @param {number} margin - Desired margin percentage
 * @param {number} [decimals=2] - Decimal places for result
 * @returns {number} Sale price
 * @example salePriceFromMargin(80, 20) => 100
 */
function salePriceFromMargin(cost, margin, decimals = 2) {
  const costNum = strToNumber(cost) || 0;
  const marginNum = strToNumber(margin) || 0;

  if (!costNum) return 0;
  if (!marginNum || marginNum >= 100) return round(costNum, decimals);

  return round(costNum / (1 - marginNum / 100), decimals);
}

// *******************************************************************************
// DISCOUNT FUNCTIONS
// *******************************************************************************

/**
 * Apply discount to a value
 * @param {number} value - Original value
 * @param {number} discount - Discount amount or percentage
 * @param {boolean} [isPercentage=true] - If true, discount is percentage; if false, discount is absolute value
 * @param {number} [decimals=2] - Decimal places for result
 * @returns {number} Discounted value
 */
function applyDiscount(value, discount, isPercentage = true, decimals = 2) {
  const valueNum = strToNumber(value) || 0;
  const discountNum = strToNumber(discount) || 0;

  if (!valueNum) return 0;
  if (!discountNum) return round(valueNum, decimals);

  if (isPercentage) {
    return subtractPercentage(valueNum, discountNum, decimals);
  } else {
    return round(valueNum - discountNum, decimals);
  }
}

/**
 * Calculate discount value from percentage
 * @param {number} value - Original value
 * @param {number} percentage - Discount percentage
 * @param {number} [decimals=2] - Decimal places for result
 * @returns {number} Discount amount
 */
function discountValue(value, percentage, decimals = 2) {
  return percentageOf(value, percentage, decimals);
}

// *******************************************************************************
// INVOICE CALCULATION FUNCTIONS
// *******************************************************************************

/**
 * Calculate item total (quantity * unit price - discount)
 * @param {Object} params - Calculation parameters
 * @param {number} params.quantity - Item quantity
 * @param {number} params.unitPrice - Unit price
 * @param {number} [params.discount=0] - Discount amount (absolute value)
 * @param {number} [params.discountPercentage=0] - Discount percentage
 * @param {number} [decimals=2] - Decimal places for result
 * @returns {number} Item total
 */
function calculateItemTotal({ quantity, unitPrice, discount = 0, discountPercentage = 0 }, decimals = 2) {
  const qty = strToNumber(quantity) || 0;
  const price = strToNumber(unitPrice) || 0;
  const discountAbs = strToNumber(discount) || 0;
  const discountPct = strToNumber(discountPercentage) || 0;

  let subtotal = round(qty * price, decimals);

  // Apply percentage discount first, then absolute discount
  if (discountPct > 0) {
    subtotal = subtractPercentage(subtotal, discountPct, decimals);
  }
  if (discountAbs > 0) {
    subtotal = round(subtotal - discountAbs, decimals);
  }

  return Math.max(0, subtotal); // Never return negative
}

/**
 * Calculate invoice totals from items
 * @param {Array} items - Invoice items array
 * @param {Object} [options] - Additional options
 * @param {number} [options.freight=0] - Freight value
 * @param {number} [options.insurance=0] - Insurance value
 * @param {number} [options.otherExpenses=0] - Other expenses
 * @param {number} [options.discount=0] - Global discount
 * @returns {Object} Totals object
 */
function calculateInvoiceTotals(items = [], options = {}) {
  const {
    freight = 0,
    insurance = 0,
    otherExpenses = 0,
    discount = 0,
  } = options;

  const productsTotal = items.reduce((sum, item) => {
    const itemTotal = item.totalValue || calculateItemTotal({
      quantity: item.quantity,
      unitPrice: item.unitValue || item.unitPrice,
      discount: item.discount,
      discountPercentage: item.discountPercentage,
    });
    return sum + itemTotal;
  }, 0);

  const subtotal = roundCurrency(productsTotal);
  const freightValue = roundCurrency(strToNumber(freight) || 0);
  const insuranceValue = roundCurrency(strToNumber(insurance) || 0);
  const otherExpensesValue = roundCurrency(strToNumber(otherExpenses) || 0);
  const discountValue = roundCurrency(strToNumber(discount) || 0);

  const invoiceTotal = roundCurrency(
    subtotal + freightValue + insuranceValue + otherExpensesValue - discountValue
  );

  return {
    productsTotal: subtotal,
    freight: freightValue,
    insurance: insuranceValue,
    otherExpenses: otherExpensesValue,
    discount: discountValue,
    invoiceTotal: Math.max(0, invoiceTotal),
  };
}

/**
 * Calculate change (troco) for payment
 * @param {number} invoiceTotal - Invoice total
 * @param {number} amountPaid - Amount paid
 * @param {number} [decimals=2] - Decimal places for result
 * @returns {number} Change amount (positive if overpaid, negative if underpaid)
 */
function calculateChange(invoiceTotal, amountPaid, decimals = 2) {
  const total = roundCurrency(strToNumber(invoiceTotal) || 0);
  const paid = roundCurrency(strToNumber(amountPaid) || 0);
  return round(paid - total, decimals);
}

/**
 * Calculate remaining balance for partial payment
 * @param {number} invoiceTotal - Invoice total
 * @param {Array|number} paymentsOrPaidAmount - Array of payment objects with amount property, or total paid amount
 * @param {number} [decimals=2] - Decimal places for result
 * @returns {number} Remaining balance
 */
function calculateRemainingBalance(invoiceTotal, paymentsOrPaidAmount = [], decimals = 2) {
  const total = roundCurrency(strToNumber(invoiceTotal) || 0);

  // Accept either array of payments or a number
  let paid = 0;
  if (Array.isArray(paymentsOrPaidAmount)) {
    paid = paymentsOrPaidAmount.reduce((sum, p) => sum + (roundCurrency(strToNumber(p.amount) || 0)), 0);
  } else {
    paid = roundCurrency(strToNumber(paymentsOrPaidAmount) || 0);
  }

  return round(total - paid, decimals);
}

// *******************************************************************************
// FORMATTING FUNCTIONS
// *******************************************************************************

/**
 * Format number with locale and options
 * @param {number} value - Value to format
 * @param {Object} [options] - Formatting options
 * @param {number} [options.decimals=2] - Decimal places
 * @param {string} [options.locale='pt-BR'] - Locale for formatting
 * @param {boolean} [options.showCurrency=false] - Show currency symbol
 * @param {string} [options.currency='BRL'] - Currency code
 * @returns {string} Formatted string
 */
function formatNumber(value, options = {}) {
  const {
    decimals = 2,
    locale = 'pt-BR',
    showCurrency = false,
    currency = 'BRL',
  } = options;

  const num = strToNumber(value) || 0;
  const fixedNum = fix(num, decimals);

  const formatOptions = {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  };

  if (showCurrency) {
    formatOptions.style = 'currency';
    formatOptions.currency = currency;
  }

  return fixedNum.toLocaleString(locale, formatOptions);
}

/**
 * Format as currency (BRL by default)
 * @param {number} value - Value to format
 * @param {string} [currency='BRL'] - Currency code
 * @param {string} [locale='pt-BR'] - Locale
 * @returns {string} Formatted currency string
 */
function formatCurrency(value, currency = 'BRL', locale = 'pt-BR') {
  return formatNumber(value, {
    decimals: 2,
    locale,
    showCurrency: true,
    currency,
  });
}

/**
 * Format as percentage
 * @param {number} value - Value to format (e.g., 15 for 15%)
 * @param {number} [decimals=2] - Decimal places
 * @param {string} [locale='pt-BR'] - Locale
 * @returns {string} Formatted percentage string
 */
function formatPercentage(value, decimals = 2, locale = 'pt-BR') {
  const num = fix(strToNumber(value) || 0, decimals);
  return `${num.toLocaleString(locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}%`;
}

/**
 * Format quantity (3 decimal places by default)
 * @param {number} value - Quantity value
 * @param {number} [decimals=3] - Decimal places
 * @param {string} [locale='pt-BR'] - Locale
 * @returns {string} Formatted quantity string
 */
function formatQuantity(value, decimals = 3, locale = 'pt-BR') {
  return formatNumber(value, { decimals, locale });
}

// *******************************************************************************
// LEGACY COMPATIBILITY FUNCTIONS
// *******************************************************************************
// These maintain backward compatibility with existing code

/**
 * @deprecated Use addPercentage instead
 */
function markup(value = 0, markupPercent = 0, decimals = 0) {
  return addPercentage(value, markupPercent, decimals || 2);
}

/**
 * @deprecated Use salePriceFromMargin instead
 */
function markdownReverse(cost = 0, markdown = 0, decimals = 0) {
  return salePriceFromMargin(cost, markdown, decimals || 2);
}

/**
 * @deprecated Use percentageOf instead
 */
function percentageCalculator(value = 0, percentage = 0, decimals = 0) {
  return percentageOf(value, percentage, decimals || 2);
}

/**
 * @deprecated Use subtractPercentage instead
 */
function discount(value = 0, percentage = 0, decimals = 0, returnAsString = false) {
  const result = subtractPercentage(value, percentage, decimals || 2);
  return returnAsString ? formatCurrency(result) : result;
}

/**
 * @deprecated Use formatCurrency instead
 */
function formatMoney(...args) {
  let value = typeof args?.[0] === 'object' ? args?.[0]?.value || 0 : args?.[0] || 0;
  let decimals = typeof args?.[0] === 'object' ? args?.[0]?.fix || 2 : args?.[1] || 2;
  let localeSymbol = typeof args?.[0] === 'object' ? args?.[0]?.localeSymbol || null : args?.[2] || null;
  let resultToStr = typeof args?.[0] === 'object' ? args?.[0]?.resultToStr || false : args?.[3] || false;

  const num = strToNumber(value) || 0;
  const fixedNum = fix(num, decimals);

  if (!resultToStr) return fixedNum;

  if (localeSymbol) {
    return formatCurrency(fixedNum, localeSymbol.toUpperCase());
  }
  return formatNumber(fixedNum, { decimals });
}

/**
 * Calculate difference markup between two values
 */
function diffMarkup(value1 = 0, value2 = 0, decimals = 0) {
  if (isNaN(value1) || isNaN(value2)) return 0;
  const v1 = Number(value1);
  const v2 = Number(value2);
  if (!v1) return 100;
  if (!v2) return -100;
  const result = 100 - (v2 / v1) * 100;
  return decimals ? fix(result, decimals) : result;
}

/**
 * Calculate difference markdown between two values
 */
function diffMarkdown(value1 = 0, value2 = 0, decimals = 0) {
  if (isNaN(value1) || isNaN(value2)) return 0;
  const v1 = Number(value1);
  const v2 = Number(value2);
  if (!v1) return 100;
  if (!v2) return -100;
  const result = -(((v1 - v2) / v1) * 100);
  return decimals ? fix(result, decimals) : result;
}

/**
 * Rule of three calculation
 */
function ruleOfThree(elementA = 0, resultA = 0, elementB = 0, decimals = 0) {
  if (isNaN(elementA) || isNaN(resultA) || isNaN(elementB)) return 0;
  const elA = Number(elementA);
  const rA = Number(resultA);
  const elB = Number(elementB);
  if (!elB) return 0;
  const result = (elB * rA) / elA;
  return decimals ? fix(result, decimals) : result;
}

/**
 * Get currency symbol (placeholder)
 */
function getCurrencySymbol(currency) {
  const symbols = {
    BRL: 'R$',
    USD: 'US$',
    EUR: '€',
    GBP: '£',
  };
  return symbols[currency?.toUpperCase()] || 'R$';
}

/**
 * Value or percentage options for selects
 */
function valueOrPercentageOptions() {
  return [
    {
      value: 'value',
      text: translateX('real'),
      label: translateX('real_symbol'),
      disabled: false,
    },
    {
      value: 'percentage',
      text: translateX('percentenge'),
      label: translateX('percentage_symbol'),
      disabled: false,
    },
  ];
}

/**
 * Get markdown from values
 */
function getMarkdown(valueFrom, valueTo, returnAsString = false) {
  const valueFromPercentage = ruleOfThree(valueTo, 100, valueFrom, 2);
  const markDown = 100 - valueFromPercentage;
  return returnAsString ? formatNumber(markDown, { decimals: 3 }) : fix(markDown, 3);
}

// *******************************************************************************
// COST COMPOSITION FUNCTIONS
// *******************************************************************************

/**
 * Cost application types for supplier cost composition
 * @type {Object}
 */
const COST_TYPES = {
  INFORMATIVE: 'informative', // Display only, does not affect any price
  COST: 'cost',               // Added on top of purchase price → builds replacement price
  PURCHASE_TAX: 'purchaseTax', // Percentage over replacement price (purchase + costs)
  SALE_TAX: 'saleTax',         // Percentage over final sale price ("por dentro" / inside-out)
};

/**
 * Payer source options for costs/taxes
 * @type {Object}
 */
const PAYER_SOURCES = {
  MUNICIPAL: 'municipal',
  STATE: 'state',
  FEDERAL: 'federal',
  OTHER: 'other',
};

/**
 * Calculate complete cost composition for a supplier product.
 *
 * Flow:
 * 1. purchasePrice (input) — price from supplier after discounts
 * 2. + cost items (type='cost', % over purchasePrice) → costSubtotal
 * 3. + purchaseTax items (type='purchaseTax', % over costSubtotal) → replacementPrice
 * 4. replacementPrice * (1 + markup/100) → baseSellingPrice
 * 5. saleTax items (type='saleTax') → finalPrice (inside-out: base / (1 - sumSaleTaxPct))
 *
 * @param {Object} params
 * @param {number} params.purchasePrice - Cost from supplier after discounts
 * @param {number} [params.markup=50] - Markup percentage
 * @param {Array} [params.appliedCosts=[]] - Array of cost items
 * @returns {Object} Calculated composition with enriched costs
 */
function calculateCostComposition({ purchasePrice = 0, markup = 50, appliedCosts = [] }) {
  const purchase = Number(purchasePrice) || 0;

  // ── Phase 1: Cost items (type='cost') — % over purchasePrice ──────────
  // Accumulate raw values WITHOUT intermediate rounding to avoid precision loss
  let rawCostSum = 0;
  const phase1 = appliedCosts.map(cost => {
    let rawValue = 0;

    if (cost.type === COST_TYPES.COST) {
      rawValue = cost.mode === 'value'
        ? (cost.value || 0)
        : purchase * (cost.percentage || 0) / 100;
      rawCostSum += rawValue;
    }

    return { ...cost, rawValue };
  });

  // costSubtotal = purchase + all direct costs (raw, no rounding)
  const rawCostSubtotal = purchase + rawCostSum;

  // ── Phase 2: Purchase tax items (type='purchaseTax') — % over costSubtotal ─
  let rawPurchaseTaxSum = 0;
  const phase2 = phase1.map(cost => {
    if (cost.type === COST_TYPES.PURCHASE_TAX) {
      const rawValue = cost.mode === 'value'
        ? (cost.value || 0)
        : rawCostSubtotal * (cost.percentage || 0) / 100;
      rawPurchaseTaxSum += rawValue;
      return { ...cost, rawValue };
    }
    return cost;
  });

  // replacementPrice = costSubtotal + purchase taxes (round only final result)
  const replacementPrice = roundCurrency(rawCostSubtotal + rawPurchaseTaxSum);

  // ── Phase 3: Markup → base selling price ──────────────────────────────
  const baseSellingPrice = roundCurrency(replacementPrice * (1 + (markup || 0) / 100));

  // ── Phase 4: Sale taxes (type='saleTax') — "por dentro" calculation ───
  let sumSaleTaxPct = 0;
  appliedCosts.forEach(cost => {
    if (cost.type === COST_TYPES.SALE_TAX && cost.mode === 'percentage') {
      sumSaleTaxPct += (cost.percentage || 0);
    }
  });

  const saleTaxDivisor = 1 - sumSaleTaxPct / 100;
  const finalPrice = saleTaxDivisor > 0
    ? roundCurrency(baseSellingPrice / saleTaxDivisor)
    : baseSellingPrice;

  // ── Phase 5: Enrich all items with rounded computedValue for display ──
  const enrichedCosts = phase2.map(cost => {
    if (cost.type === COST_TYPES.SALE_TAX) {
      const computedValue = cost.mode === 'percentage'
        ? roundCurrency(finalPrice * (cost.percentage || 0) / 100)
        : roundCurrency(cost.value || 0);
      return { ...cost, computedValue };
    }

    if (cost.type === COST_TYPES.INFORMATIVE) {
      const computedValue = cost.mode === 'percentage'
        ? roundCurrency(purchase * (cost.percentage || 0) / 100)
        : roundCurrency(cost.value || 0);
      return { ...cost, computedValue };
    }

    // COST and PURCHASE_TAX — display the rounded raw value
    return { ...cost, computedValue: roundCurrency(cost.rawValue || 0) };
  });

  // Clean rawValue from output (internal only)
  const cleanCosts = enrichedCosts.map(({ rawValue, ...rest }) => rest);

  // Totals for display
  const totalCostValue = roundCurrency(rawCostSum);
  const totalPurchaseTaxValue = roundCurrency(rawPurchaseTaxSum);
  const totalSaleTaxValue = cleanCosts
    .filter(c => c.type === COST_TYPES.SALE_TAX)
    .reduce((sum, c) => sum + (c.computedValue || 0), 0);

  return {
    purchasePrice: roundCurrency(purchase),
    totalCostValue,
    costSubtotal: roundCurrency(rawCostSubtotal),
    totalPurchaseTaxValue,
    replacementPrice,
    markup: markup || 0,
    baseSellingPrice,
    totalSaleTaxValue: roundCurrency(totalSaleTaxValue),
    finalPrice,
    appliedCosts: cleanCosts,
  };
}

// *******************************************************************************
// SERVICE OBJECT
// *******************************************************************************
const financialService = {
  // Constants
  ROUNDING_MODES,
  DEFAULT_DECIMALS,

  // Rounding functions
  round,
  roundUp,
  roundDown,
  truncate,
  roundCurrency,
  fix,

  // Percentage functions
  percentageOf,
  valueToPercentage,
  addPercentage,
  subtractPercentage,
  calculateMarkup,
  calculateMargin,
  salePriceFromMarkup,
  salePriceFromMargin,

  // Discount functions
  applyDiscount,
  discountValue,

  // Invoice calculations
  calculateItemTotal,
  calculateInvoiceTotals,
  calculateChange,
  calculateRemainingBalance,

  // Formatting functions
  formatNumber,
  formatCurrency,
  formatPercentage,
  formatQuantity,

  // Legacy compatibility (deprecated)
  markup,
  markdownReverse,
  percentageCalculator,
  discount,
  formatMoney,
  diffMarkup,
  diffMarkdown,
  ruleOfThree,
  getCurrencySymbol,
  valueOrPercentageOptions,
  getMarkdown,

  // Cost composition
  COST_TYPES,
  PAYER_SOURCES,
  calculateCostComposition,

  // Alias for backward compatibility
  salePriceWithMarkup: salePriceFromMarkup,
  percentageToValue: percentageOf,
};

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export default financialService;

// Named exports for tree-shaking
export {
  ROUNDING_MODES,
  DEFAULT_DECIMALS,
  round,
  roundUp,
  roundDown,
  truncate,
  roundCurrency,
  fix,
  percentageOf,
  valueToPercentage,
  addPercentage,
  subtractPercentage,
  calculateMarkup,
  calculateMargin,
  salePriceFromMarkup,
  salePriceFromMargin,
  applyDiscount,
  discountValue,
  calculateItemTotal,
  calculateInvoiceTotals,
  calculateChange,
  calculateRemainingBalance,
  formatNumber,
  formatCurrency,
  formatPercentage,
  formatQuantity,
  COST_TYPES,
  PAYER_SOURCES,
  calculateCostComposition,
};
